/**
 * Pre-registration queries and mutations for TanStack Query (Vendor App)
 *
 * This module provides all the necessary queries and mutations for pre-registration management.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import { computed, toValue, type MaybeRefOrGetter } from 'vue'
import { queryKeys } from '../keys'
import { queryGet, queryPatch } from '../utils/request'

// Type definitions
export interface Preregistration {
  loja_cnpj: string
  loja_razao_social: string
  device_count: number
  tenant_name: string | null
  tenant_id: number | null
  assigned: boolean
}

export interface PreregistrationListParams {
  limit?: number
  offset?: number
  assigned_only?: boolean
  unassigned_only?: boolean
  loja_cnpj_filter?: string
  loja_razao_social_filter?: string
  sat_serial_filter?: string
}

export interface PreregistrationDevice {
  id: number
  loja_cnpj: string
  loja_razao_social: string
  sat_serial_number: string
  assigned: boolean
  oauth2_device_id?: string | null
  tenant_id?: number | null
  tenant_name?: string | null
  created_utc: string
  assigned_at_utc?: string | null
}

export interface PreregistrationStoreDetails {
  loja_cnpj: string
  loja_razao_social: string
  devices: Array<{
    sat_serial: string
    device_type: string
    last_seen?: string
  }>
  tenant_id: number | null
  tenant_name: string | null
}

export interface AssignPreregistrationParams {
  loja_cnpj: string
  tenant_id: number
}

// Cache configuration
const CACHE_TIMES = {
  PREREGISTRATIONS: 2 * 60 * 1000, // 2 minutes
  STORE_DETAILS: 1 * 60 * 1000, // 1 minute
}

/**
 * QUERIES
 */

/**
 * Query hook to fetch a list of pre-registrations
 */
export const usePreregistrationsQuery = (params: MaybeRefOrGetter<PreregistrationListParams> = {}) => {
  return useQuery({
    queryKey: computed(() => queryKeys.preregistrationList(toValue(params))),
    queryFn: async (): Promise<Preregistration[]> => {
      const queryParams = toValue(params)
      return queryGet<Preregistration[]>('/v2/vendor/preregistrations', { params: queryParams })
    },
    staleTime: CACHE_TIMES.PREREGISTRATIONS,
  })
}

/**
 * Query hook to fetch store details by CNPJ
 */
export const usePreregistrationStoreQuery = (lojaCnpj: MaybeRefOrGetter<string | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const cnpj = toValue(lojaCnpj)
      return cnpj ? [...queryKeys.preregistrations(), 'store', cnpj] : ['preregistrations', 'store', 'null']
    }),
    queryFn: async (): Promise<PreregistrationStoreDetails> => {
      const cnpj = toValue(lojaCnpj)
      if (!cnpj) throw new Error('CNPJ is required')
      return queryGet<PreregistrationStoreDetails>(`/v2/vendor/preregistrations/stores/${cnpj}`)
    },
    staleTime: CACHE_TIMES.STORE_DETAILS,
    enabled: computed(() => !!toValue(lojaCnpj)),
  })
}

/**
 * Query hook to fetch store device details by CNPJ
 * Returns an array of device registrations for a specific store
 */
export const usePreregistrationStoreDevicesQuery = (lojaCnpj: MaybeRefOrGetter<string | null | undefined>) => {
  return useQuery({
    queryKey: computed(() => {
      const cnpj = toValue(lojaCnpj)
      return cnpj ? [...queryKeys.preregistrations(), 'store-devices', cnpj] : ['preregistrations', 'store-devices', 'null']
    }),
    queryFn: async (): Promise<PreregistrationDevice[]> => {
      const cnpj = toValue(lojaCnpj)
      if (!cnpj) throw new Error('CNPJ is required')
      return queryGet<PreregistrationDevice[]>(`/v2/vendor/preregistrations/stores/${cnpj}`)
    },
    staleTime: CACHE_TIMES.STORE_DETAILS,
    enabled: computed(() => !!toValue(lojaCnpj)),
  })
}

/**
 * MUTATIONS
 */

/**
 * Mutation hook to assign a pre-registration to a tenant
 */
export const useAssignPreregistrationMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (params: AssignPreregistrationParams): Promise<any> => {
      return queryPatch<any>('/v2/vendor/preregistrations/assign', params)
    },
    onSuccess: () => {
      // Invalidate pre-registrations list to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.preregistrations() })
    },
  })
}

/**
 * Hook that combines pre-registration queries and mutations for convenient usage
 */
export const usePreregistrationOperations = () => {
  return {
    // Queries
    usePreregistrationsQuery,
    usePreregistrationStoreQuery,

    // Mutations
    useAssignPreregistrationMutation,
  }
}
